import React, { useMemo, useState } from 'react';
import { useCurrency } from '../../contexts/CurrencyContext';
import imovelFallback from '../../assets/imoveis/01.webp';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import contactBg from '../../assets/backgrounds/contato.png';

const Page = styled.div`
  /* ocupa quase toda a largura em telas grandes, com limite alto */
  max-width: min(95vw, 1600px);
  margin: 24px auto 84px; /* ABAIXO do hero, sem sobreposição */
  padding: 0 24px;
`;

// Hero no topo — replicando o visual da página de Contato
const Hero = styled.div`
  position: relative;
  width: 100%;
  height: 320px; /* hero suficiente, sem empurrar a busca */
  background: linear-gradient(rgba(0, 0, 0, 0.72), rgba(0, 0, 0, 0.88)),
              url(${contactBg}) center/cover no-repeat;
  margin-top: 0;            /* não sobrepor navbar */
  display: flex;
  align-items: flex-end;     /* fixa conteúdo na parte de baixo do hero */
  justify-content: center;
  color: #fff;
  z-index: 1;
  padding-bottom: 24px;      /* espaço do rodapé do hero */
`;

const HeroInner = styled.div`
  max-width: min(95vw, 1600px);
  margin: 0 auto;
  width: 100%;
  padding: 0 24px;
  display: grid;
  grid-template-columns: 1fr 1fr; /* título à esquerda, subtítulo à direita */
  align-items: center;
  gap: 16px;

  h1 {
    margin: 0;
    font-size: 48px;
    font-weight: 600;
    color: #ffffff;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
  }
  p {
    margin: 0;
    font-size: 24px;
    color: #ffffff;
    opacity: 0.9;
    text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
    justify-self: end; /* alinha subtítulo à direita */
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr; /* empilha no mobile */
    h1 { font-size: 36px; }
    p { font-size: 20px; justify-self: start; }
  }

  @media (max-width: 480px) {
    h1 { font-size: 28px; }
    p { font-size: 16px; }
  }
`;
const Header = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  align-items: start;
  gap: 8px;
  margin-bottom: 32px; /* mais respiro antes do grid de comparação */
  @media (min-width: 900px) {
    grid-template-columns: 1fr auto; /* filtro à esquerda, ações à direita */
    gap: 16px;
    align-items: center;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  justify-self: end;
  margin-top: 6px;
`;

const Button = styled.button`
  padding: 10px 16px;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  font-weight: 600;
  transition: all .2s ease;

  &.secondary {
    background: #f1f5f9;
    color: #34574A;
    border: 1px solid #e2e8f0;
  }

  &.primary {
    background: #BA824F;
    color: #fff;
  }

  &:hover { filter: brightness(.95); }
`;

const CardGrid = styled.div`
  overflow-x: auto;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  box-shadow: 0 12px 28px rgba(0,0,0,.06);
  scrollbar-width: thin;
  -webkit-overflow-scrolling: touch;
  margin-top: 24px; /* mais respiro abaixo do filtro */
  padding: 8px 12px 12px; /* dá mais respiro para não parecer cortado */
`;

const TopRow = styled.div`
  display: grid;
  grid-template-columns: 280px repeat(${(p) => p.$cols}, minmax(${(p) => p.$wide ? 360 : 320}px, 1fr));
  gap: 0;
  border-bottom: 1px solid #edf2f7;
`;

const AttrHeader = styled.div`
  padding: 20px 22px;
  font-weight: 700;
  color: #2d3748;
  background: #f8fafc;
  border-top-left-radius: 16px;
`;

const ItemHead = styled.div`
  padding: 20px 22px;
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 240px;
  border-left: 1px solid #edf2f7;

  &:first-of-type { border-top-right-radius: 16px; }

  img { width: 92px; height: 64px; object-fit: cover; border-radius: 10px; box-shadow: 0 6px 16px rgba(0,0,0,.08); }
  .title { font-weight: 700; color: #1f2937; }
  .price { font-size: .92rem; color: #34574A; opacity: .95; margin-top: 4px; font-weight: 700; }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 280px repeat(${(p) => p.$cols}, minmax(${(p) => p.$wide ? 360 : 320}px, 1fr));
  border-top: 1px solid #f1f5f9;
`;

const CellLabel = styled.div`
  padding: 18px 22px;
  background: #f9fbfd;
  color: #4a5568;
  font-weight: 600;
`;

const Cell = styled.div`
  padding: 18px 22px;
  min-width: 240px;
  border-left: 1px solid #f1f5f9;
  color: #2d3748;
  display: flex;
  align-items: center;
  line-height: 1.5;
`;

const SearchBar = styled.div`
  margin-top: 6px;    /* valor original, sem interferir no input */
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;

  input[type="text"] {
    flex: 1 1 320px;
    max-width: 520px;
    padding: 10px 12px;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    outline: none;
    font-size: .98rem;
    transition: box-shadow .2s ease, border-color .2s ease;
  }

  input[type="text"]:focus {
    border-color: #cbd5e1;
    box-shadow: 0 0 0 3px rgba(186,130,79,.15);
  }

  .result-info {
    color: #475569;
    font-weight: 600;
  }
`;

const ComparePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const { currencySymbol, exchangeRate, currency } = useCurrency();

  // Recebe itens via state ou localStorage como fallback e normaliza o formato
  const stateItems = (location.state && Array.isArray(location.state.items)) ? location.state.items : [];
  let items = stateItems;
  if (!items.length) {
    try {
      const saved = localStorage.getItem('compare_items');
      if (saved) items = JSON.parse(saved);
    } catch (_) {}
  }

  // Normalizador: aceita itens no formato novo ({title, price, ...}) ou antigo ({tituloIMOVEL, valorIMOVEL, fotos, ...})
  const normalize = (it) => {
    if (!it || typeof it !== 'object') return null;
    const pickFirst = (...vals) => {
      for (const v of vals) {
        if (v === undefined || v === null) continue;
        if (typeof v === 'string' && v.trim() === '') continue;
        return v;
      }
      return '';
    };
    const absolutize = (src) => {
      if (!src || typeof src !== 'string') return '';
      if (src.startsWith('http') || src.startsWith('data:')) return src;
      const cleaned = src.replace(/^\.*\/+/, '').replace(/^images\//, 'images/');
      const host = (import.meta.env.VITE_PUBLIC_ASSETS_HOST || '').replace(/\/$/, '');
      const base = host || String(import.meta.env.VITE_URL_API || '').replace(/\/api\/?$/, '');
      return `${base}/${cleaned}`;
    };
    // resolve imagem
    let img = it.image;
    if (!img) {
      const f = Array.isArray(it.fotos) ? it.fotos[0] : null;
      img = typeof f === 'string' ? f : (f?.urlFOTO || undefined);
    }
    img = absolutize(img || '');
    const normalized = {
      id: pickFirst(it.id, it.codigoIMOVEL, it.idIMOVEL, it?.dadosBasicos?.codigoIMOVEL),
      title: pickFirst(it.title, it.tituloIMOVEL, it?.dadosBasicos?.tituloIMOVEL, 'Sem título'),
      price: pickFirst(it.price, it.valorIMOVEL, it?.dadosBasicos?.valorIMOVEL, ''),
      location: pickFirst(it.location, it.enderecoIMOVEL, it?.dadosBasicos?.enderecoIMOVEL, ''),
      bedrooms: pickFirst(it.bedrooms, it.quartosIMOVEL, it?.dadosBasicos?.quartosIMOVEL, ''),
      bathrooms: pickFirst(it.bathrooms, it.banheirosIMOVEL, it?.dadosBasicos?.banheirosIMOVEL, ''),
      area: pickFirst(it.area, it.metragemIMOVEL, it?.dadosBasicos?.metragemIMOVEL, ''),
      garage: pickFirst(it.garage, it.vagasgaragemIMOVEL, it?.dadosBasicos?.vagasgaragemIMOVEL, ''),
      image: img,
    };

    return normalized;
  };

  items = Array.isArray(items) ? items.map(normalize).filter(Boolean) : [];

  const clearAndBack = () => {
    try { localStorage.removeItem('compare_items'); } catch (_) {}
    navigate(-1);
  };

  if (!items || !items.length) {
    return (
      <>
        <Hero>
          <HeroInner>
            <h1>Comparar Imóveis</h1>
            <p>Selecione ao menos dois imóveis para visualizar a comparação lado a lado.</p>
          </HeroInner>
        </Hero>
        <Page>
          <Header>
            <h2 style={{ margin: 0 }}>Comparação de imóveis</h2>
          </Header>
          <p style={{ marginBottom: 16 }}>
            Nenhum imóvel para comparar no momento. Volte e selecione pelo menos dois itens para iniciar a comparação.
          </p>
          <Button className="primary" onClick={() => navigate(-1)}>Voltar</Button>
        </Page>
      </>
    );
  }

  const headers = [
    { key: 'title', label: 'Título' },
    { key: 'price', label: 'Preço' },
    { key: 'location', label: 'Localização' },
    { key: 'bedrooms', label: 'Quartos' },
    { key: 'bathrooms', label: 'Casas de banho' },
    { key: 'area', label: 'Área (m²)' },
    { key: 'garage', label: 'Garagem' },
  ];

  const visibleItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter(it =>
      [it.title, it.location]
        .filter(Boolean)
        .some(v => String(v).toLowerCase().includes(q))
    );
  }, [items, query]);

  const toMoney = (v) => {
    if (v === undefined || v === null || v === '') return '—';
    const base = Number(v);
    if (!Number.isFinite(base)) return String(v);
    const rate = Number(exchangeRate) || 1;
    const converted = Math.round((base * rate + Number.EPSILON) * 100) / 100;
    const symbol = currencySymbol || currency || '€';
    try {
      return `${symbol} ${converted.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    } catch {
      return `${symbol} ${converted}`;
    }
  };

  return (
    <>
      <Hero>
        <HeroInner>
          <h1>Comparar Imóveis</h1>
          <p>Como podemos ser úteis?<br
          />Estamos aqui para ajudar.</p>
        </HeroInner>
      </Hero>
    <Page>
      <Header>
        <SearchBar>
          <input
            type="text"
            placeholder="Pesquisar por título ou localização..."
            value={query}
            onChange={(e)=>setQuery(e.target.value)}
            onKeyDown={(e)=>{ if (e.key === 'Escape') setQuery(''); }}
          />
          {query && (
            <Button className="secondary" onClick={()=>setQuery('')}>Limpar busca</Button>
          )}
          <span className="result-info">{visibleItems.length} resultado(s)</span>
        </SearchBar>
        <Actions>
          <Button className="secondary" onClick={clearAndBack}>Limpar</Button>
          <Button className="primary" onClick={() => navigate(-1)}>Voltar</Button>
        </Actions>
      </Header>

      <CardGrid>
        <TopRow $cols={visibleItems.length} $wide={visibleItems.length > 4}>
          <AttrHeader>Atributo</AttrHeader>
          {visibleItems.map((item, idx) => (
            <ItemHead key={idx}>
              <img
                src={item.image || imovelFallback}
                alt={item.title}
                onError={(e)=>{ e.currentTarget.src = imovelFallback; e.currentTarget.onerror = null; }}
                onLoad={(e)=>{
                  const img = e.currentTarget;
                  // se a imagem for 1x1 (ou muito pequena/transparente), usa fallback
                  if ((img.naturalWidth <= 2 && img.naturalHeight <= 2)) {
                    img.src = imovelFallback;
                  }
                }}
                style={{ background: '#e5e7eb' }}
              />
              <div>
                <div className="title">{item.title}</div>
                <div className="price">{toMoney(item.price)}</div>
              </div>
            </ItemHead>
          ))}
        </TopRow>

        {headers.map((h, i) => (
          <Row key={h.key} $cols={visibleItems.length} $wide={visibleItems.length > 4} style={{background: i % 2 === 0 ? '#fff' : '#fcfdfd'}}>
            <CellLabel>{h.label}</CellLabel>
            {visibleItems.map((item, idx) => (
              <Cell key={idx}>{
                h.key === 'price'
                  ? toMoney(item[h.key])
                  : (item[h.key] !== undefined && item[h.key] !== null && String(item[h.key]).trim() !== ''
                      ? String(item[h.key])
                      : '—')
              }</Cell>
            ))}
          </Row>
        ))}
      </CardGrid>
    </Page>
    </>
  );
};

export default ComparePage;
